import React, { useEffect, useRef } from 'react';
import { Box, Typography, Collapse, List } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TreeView } from '@material-ui/lab';
import { Folder, ExpandLess, ExpandMore, FileCopyOutlined, DeleteOutline } from '@material-ui/icons';
import { usePopupState, bindToggle } from 'material-ui-popup-state/hooks';
import { useTranslate } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import ListItemPopover from '../common/list-item-popover';
import ListItemPopoverItem from '../common/list-item-popover/item';
import GreenSwitch from '../CustomizedUIs/GreenSwitch';
import StyledTreeItem from './tree-item';
import classnames from 'classnames';
import CreateInfoPage from '../common/dialog-create-language';
import { markSelectedLeaf } from '../utils';
import { selectNode, changeTreeData, selectInfoPageLang, changeInfoPageDirtyStatus } from '../redux/actions/tree-action';


const useStyles = makeStyles(theme => ({
    container: {
        backgroundColor: '#ffffff',
        borderRadius: 4,
        border: 'solid 1px #c1e5fa',
        paddingTop: theme.spacing(2.5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        width: 450,
    },
    root: {
        flexGrow: 1,
    },
    parentItem: {
        marginBottom: theme.spacing(2),
        backgroundColor: '#f3f7fa',
        borderRadius: theme.spacing(1),
        paddingBottom: 4,
        position: 'relative'
    },
    list: {
        overflowX: 'auto',
        scrollBehavior: 'smooth'
    },
    collapseIcon: {
        fontSize: '22px !important',
        color: '#4f5064'
    }
}));

const rootTreeStyle = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
        padding: '9px 20px',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        position: 'relative'
    },
    containerExpand: {
        backgroundColor: theme.overrides.MuiButton.textPrimary.color,
    },
    label: {
        fontSize: 11,
        marginLeft: 8,
        marginTop: 6,
        fontFamily: 'UniviaPro',
        color: '#4f5064',
        lineHeight: '13.8px',
        letterSpacing: "0.11px"
    },
    labelExpand: {
        color: '#FFFFFF'
    },
    labelNonActive: {
        opacity: 0.7
    },
    iconActiveExpand: {
        fontSize: 22,
        color: '#ffffff',
    },
    iconActiveNonExpand: {
        fontSize: 22,
        color: theme.overrides.MuiButton.textPrimary.color,
    },
    iconNonActive: {
        fontSize: 22,
        color: theme.overrides.MuiButton.textPrimary.color,
        opacity: 0.7
    },
    arrowIcon: {
        fontSize: 22,
        position: 'absolute',
        right: 10,
        bottom: 7,
        opacity: 0.6,
        color: '#4f5064'
    },
    arrowIconActiveExpand: {
        color: '#ffffff',
        opacity: 0.6
    },
    arrowIconNonActiveExpand: {
        opacity: 1
    },
    arrowIconNonActive: {
        fontSize: 22,
        position: 'absolute',
        right: 10,
        bottom: 7,
        opacity: 0.42,
        color: '#4f5064'
    },
}));

function RootTree({ item, onClick, onContextMenu, defaultSystemLanguage }) {
    const classes = rootTreeStyle();
    const { expand, language } = item;

    const getContainerClasses = () => {
        if (expand) return classnames(classes.container, classes.containerExpand);
        return classes.container;
    }

    const getIconClasses = () => {
        if (expand) return classes.iconActiveExpand;
        if (item.active) return classes.iconActiveNonExpand;
        return classes.iconNonActive;
    }

    const getLabelClasses = () => {
        if (expand) return classnames(classes.label, classes.labelExpand);
        if (!item.active) return classnames(classes.label, classes.labelNonActive);
        return classes.label;
    }

    const getArrowClasses = () => {
        if (item.active) {
            return classnames(classes.arrowIcon, classes.arrowIconActiveExpand);
        } else {
            return classnames(classes.arrowIcon, classes.arrowIconNonActiveExpand);
        }
    }

    const getArrowActiveClasses = () => {
        if (item.active) {
            return classnames(classes.arrowIcon);
        } else {
            return classnames(classes.arrowIconNonActive);
        }
    }

    return (
        <Box className={getContainerClasses()} onClick={onClick} onContextMenu={onContextMenu}>
            <Folder className={getIconClasses()} />
            <Typography className={getLabelClasses()}>{language.name === defaultSystemLanguage[0].value ? 'Default' : language.name}</Typography>
            {expand ? <ExpandLess className={getArrowClasses()} /> : <ExpandMore className={getArrowActiveClasses()} />}
        </Box>
    )
}

function SingleTree({ item, onClick, treeIndex, defaultSystemLanguage }) {
    const classes = useStyles();
    const treeRef = useRef(null);
    const translate = useTranslate();
    const dispatch = useDispatch();
    const { tree, nodeSelected, isEditing } = useSelector(state => state.tree);
    const popupStateParentLevel = usePopupState({ variant: 'popper', popupId: 'tree-level-1' });

    const handleChange = () => {
        for (let i = 0; i < tree.length; i++) {
            if (tree[i].id === item.id)
                tree[i].active = true
            else
                tree[i].active = false
        }
        dispatch(changeTreeData(tree))
        dispatch(changeInfoPageDirtyStatus(true));

    };

    const onScroll = (treeRef) => {
        const width = 442;
        if (treeRef && treeRef.current) {
            const scrollWidth = treeRef.current.scrollWidth;
            treeRef.current.scrollLeft = (scrollWidth - width - 30);
        }
    }

    const onSelect = (node) => {
        const newTree = markSelectedLeaf(tree, node.id);
        const nodeIdStr = node.id.toString();

        const indexOfId = newTree[treeIndex].expandIds.findIndex(id => id === nodeIdStr);
        if (indexOfId === -1) {
            // Is Closing
            const expandIds = newTree[treeIndex].expandIds.map(item => item);
            expandIds.push(nodeIdStr);
            newTree[treeIndex].expandIds = expandIds;
        } else {
            // Is Opening
            newTree[treeIndex].expandIds.splice(indexOfId, 1);
        }

        dispatch(selectNode(node));
        dispatch(changeTreeData(newTree));
        setTimeout(() => {
            onScroll(treeRef);
        }, 100);
    }

    const onContextMenu = (event) => {
        event.preventDefault();
        if (isEditing) {
            bindToggle(popupStateParentLevel).onClick(event);
        }
    }

    const renderTree = (node) => (
        <StyledTreeItem
            infoPageLanguage={item.id}
            key={node.id}
            node={node}
            nodeId={node.id + ''}
            isSelected={nodeSelected && nodeSelected.id === node.id}
            onClick={() => onSelect(node)}
            treeIndex={treeIndex}
        >
            {Array.isArray(node.children) ? node.children.map((node) => renderTree(node)) : null}
        </StyledTreeItem>
    );

    const handleOpenAddLanguage = () => {
        dispatch(selectInfoPageLang(item));
        popupStateParentLevel.close()
    }

    const handDeleteLanguage = () => {
        for (let i = 0; i < tree.length; i++) {
            if (item.active === true)
                break
            if (item.id === tree[i].id)
                tree.splice(i, 1)
        }
        dispatch(changeTreeData(tree));
        dispatch(changeInfoPageDirtyStatus(true));
    }

    return (
        <Box key={item.id} className={classes.parentItem}>
            <RootTree defaultSystemLanguage={defaultSystemLanguage} item={item} onClick={() => onClick(item.id)} onContextMenu={onContextMenu} />
            <Box>
                <Collapse in={item.expand}>
                    <Box mx={0.5} className={classes.list} ref={treeRef}>
                        {item.children.map(node => (
                            renderTree(node)
                        ))}
                    </Box>
                </Collapse>
            </Box>
            <ListItemPopover popupState={popupStateParentLevel}>
                <List component="nav">
                    <ListItemPopoverItem
                        label={translate('resources.infoPage.copyOrAddLanguage')}
                        icon={FileCopyOutlined}
                        onClick={() => handleOpenAddLanguage()}
                    />
                    {
                        (item.active === false && item.language.name !== 'Swedish') &&
                        <ListItemPopoverItem
                            label={translate('pos.common.delete')}
                            icon={DeleteOutline}
                            onClick={() => handDeleteLanguage()}
                        />
                    }
                    <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor='#f3f7fa' mx={1} pl='14px' pr='13px' py='3px'>
                        <Typography style={{ fontSize: 9, fontFamily: 'UniviaPro-Bold', letterSpacing: 0.36 }}>{translate('resources.infoPage.active')}</Typography>
                        <GreenSwitch size="small" disabled={item.active ? true : false} checked={item.active} onClick={handleChange} />
                    </Box>
                </List>
            </ListItemPopover>
        </Box>
    );
}

export default function CustomizeTreeView({ ...props }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    // STATE
    const { tree, selectedInfoPageLang, nodeSelected } = useSelector(state => state.tree);
    // END STATE

    const onClick = (nodeId) => {
        const newTree = tree.map(item => ({
            ...item,
            expand: item.id === nodeId ? !item.expand : item.expand,
            // preview: item.id === nodeId
        }));
        dispatch(changeTreeData(newTree));
    }

    useEffect(() => {
        // Should toggle the flag preview
        if (nodeSelected) {
            const callback = function (children, index) {
                if (!children) children = [];
                for (let i = 0; i < children.length; i++) {
                    if (nodeSelected.id === children[i].id) tree[index].preview = true;
                    callback(children[i].children, index);
                }
            }

            for (let i = 0; i < tree.length; i++) {
                tree[i].preview = false;
                callback(tree[i].children, i);
            }
        }
    }, [nodeSelected]);

    return (
        <Box className={tree.length > 0 ? classes.container : null} onContextMenu={e => e.preventDefault()}>
            {
                tree.map((item, index) => (
                    <TreeView
                        className={classes.root}
                        defaultCollapseIcon={<ExpandLess className={classes.collapseIcon} />}
                        defaultExpandIcon={<ExpandMore className={classes.collapseIcon} style={{ opacity: 0.42 }} />}
                        expanded={item.expandIds}
                        key={item.id.toString()}
                    >
                        <SingleTree
                            item={item}
                            onClick={onClick}
                            treeIndex={index}
                            {...props}
                        />
                    </TreeView>
                ))
            }
            <CreateInfoPage
                open={selectedInfoPageLang !== null}
                onClose={() => dispatch(selectInfoPageLang(null))}
            />
        </Box>
    );
}

CustomizeTreeView.propTypes = {
};
